import { Box, Button, IconButton, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getMediasError,
  getMediaStatus,
  getMediaSuccess,
  addMedia,
  updateStatus,
} from "../../features/mediaSlice";
import { useNavigate } from "react-router-dom";
import { useRestaurant } from "../../hooks/RestaurantContext";
import BackButton from "../../components/backButton";
import Header from "../../components/Header";
import RestaurantAppBar from "../../components/restaurantTopBar";
import { useTranslation } from "react-i18next";
import SharedMediaSwitch from "../../components/sharedMediaSwitch";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif"];

const AddMedia = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const [list, setList] = useState([]);
  // A restaurant's own uploads stay exclusive unless sharing is turned on.
  const [shared, setShared] = useState(false);
  const fileInputRef = useRef(null);
  const status = useSelector(getMediaStatus);
  const error = useSelector(getMediasError);
  const success = useSelector(getMediaSuccess);
  const isSubmitting = status === "loading";

  useEffect(() => {
    if (status === "addSuccess") {
      toast.success(success);
      dispatch(updateStatus());
      navigate("/media");
    } else if (status === "addError") {
      toast.error(error);
    }
  }, [status, error, success, dispatch, navigate]);

  const addFiles = (incomingFiles) => {
    const validFiles = Array.from(incomingFiles).filter((file) =>
      ACCEPTED_TYPES.includes(file.type)
    );
    const rejectedCount = incomingFiles.length - validFiles.length;
    if (rejectedCount > 0) {
      toast.error(t("media.unsupportedFilesSkipped", { count: rejectedCount }));
    }
    if (validFiles.length > 0) {
      setList((prevList) => [...prevList, ...validFiles]);
    }
  };

  const handleFileInputChange = (event) => {
    if (event.target.files?.length) addFiles(event.target.files);
    event.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setList((prevList) => prevList.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (list.length === 0) return;
    const formData = new FormData();
    formData.append("type", "image");
    list.forEach((file) => formData.append("files", file));
    formData.append("mediaType", "image");
    formData.append("shared", shared ? "true" : "false");
    dispatch(addMedia({ formData, type: "image", restaurantId }));
  };

  return (
    <div className="main-application">
      <RestaurantAppBar />
      <Box m="20px" className="main-application">
        <BackButton />
        <Header title={t("headers.addMedia")} subtitle={t("headers.addMediaSub")} />
        <Box sx={{ mt: 2 }}>
          <SharedMediaSwitch shared={shared} onChange={setShared} />
        </Box>

        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            mt: 2,
            p: 4,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            cursor: "pointer",
            transition: "border-color 0.15s",
            "&:hover": { borderColor: "primary.main" },
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: "primary.main" }} />
          <Typography variant="body1" fontWeight={600}>
            {t("media.dropzoneHint")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("media.dropzoneFormats")}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.gif"
            style={{ display: "none" }}
            onChange={handleFileInputChange}
          />
        </Box>

        {list.length > 0 && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("media.filesSelected", { count: list.length })}
            </Typography>
            <Button
              type="submit"
              color="secondary"
              variant="contained"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? t("common.loading") : t("common.submit")}
            </Button>
          </Box>
        )}

        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 2,
          }}
        >
          {list.map((file, index) => {
            const src = URL.createObjectURL(file);
            return (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  aspectRatio: "1",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.04)"
                      : "#f7f7f7",
                }}
              >
                <img
                  src={src}
                  alt={file.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <IconButton
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "rgba(255,255,255,0.9)",
                    width: 28,
                    height: 28,
                    "&:hover": { bgcolor: "white" },
                  }}
                >
                  <CloseIcon sx={{ color: "error.main", fontSize: 18 }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Box>
    </div>
  );
};

export default AddMedia;
